import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Pagination,
  Tooltip,
  Badge,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Pause,
  PlayArrow,
  Star,
  StarBorder,
  FilterList,
  Search
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const P2PPartnerManagement = ({ onUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [actionData, setActionData] = useState({ reason: '', notes: '', duration: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    status: '',
    country: '',
    is_verified: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });

  useEffect(() => {
    fetchPartners();
  }, [filters, pagination.current_page]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const { default: apiClient } = await import('../../request/http-common');
      
      const params = new URLSearchParams({
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...filters
      });

      const response = await apiClient.get(`/api/admin/p2p/partners?${params}`);
      
      // Debug logging to see what we're getting
      console.log('P2P Partners API Response:', response);
      console.log('Response data:', response.data);
      
      if (response.data && response.data.success && response.data.partners) {
        console.log('Partners data:', response.data.partners);
        setPartners(response.data.partners.data || []);
        setPagination({
          current_page: response.data.partners.current_page || 1,
          per_page: response.data.partners.per_page || 10,
          total: response.data.partners.total || 0,
          last_page: response.data.partners.last_page || 1
        });
      } else {
        // Handle case where API returns error or unexpected structure
        console.warn('Unexpected API response structure:', response.data);
        setPartners([]);
        setPagination({
          current_page: 1,
          per_page: 10,
          total: 0,
          last_page: 1
        });
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      // Set empty state to prevent crashes
      setPartners([]);
      setPagination({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerDetails = async (partnerId) => {
    try {
      const { default: apiClient } = await import('../../request/http-common');
      const response = await apiClient.get(`/api/admin/p2p/partners/${partnerId}`);
      
      if (response.data && response.data.success) {
        setSelectedPartner(response.data.partner);
        setDialogOpen(true);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch partner details');
      }
    } catch (error) {
      console.error('Error fetching partner details:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to fetch partner details',
        severity: 'error'
      });
    }
  };

  const handleAction = async (action, partnerId) => {
    setCurrentAction({ action, partnerId });
    setActionData({ reason: '', notes: '', duration: '' });
    setActionDialogOpen(true);
  };

  const executeAction = async () => {
    // Validation
    if ((currentAction?.action === 'reject' || currentAction?.action === 'suspend') && !actionData.reason.trim()) {
      setSnackbar({
        open: true,
        message: 'Reason is required for this action',
        severity: 'error'
      });
      return;
    }

    try {
      setActionLoading(true);
      const { default: apiClient } = await import('../../request/http-common');
      const { action, partnerId } = currentAction;
      
      let endpoint = `/api/admin/p2p/partners/${partnerId}/${action}`;
      let data = { admin_notes: actionData.notes };

      if (action === 'reject') {
        data.rejection_reason = actionData.reason;
      } else if (action === 'suspend') {
        data.suspension_reason = actionData.reason;
        if (actionData.duration) {
          data.suspension_duration = parseInt(actionData.duration);
        }
      }

      const response = await apiClient.post(endpoint, data);
      
      if (response.data && response.data.success) {
        // Success feedback
        const actionMessages = {
          approve: 'Partner approved successfully',
          reject: 'Partner rejected successfully', 
          suspend: 'Partner suspended successfully',
          reactivate: 'Partner reactivated successfully'
        };

        setSnackbar({
          open: true,
          message: actionMessages[action] || 'Action completed successfully',
          severity: 'success'
        });

        // Refresh data and close dialog
        fetchPartners();
        onUpdate && onUpdate();
        setActionDialogOpen(false);
        setActionData({ reason: '', notes: '', duration: '' });
      } else {
        throw new Error(response.data?.message || 'Action failed');
      }
    } catch (error) {
      console.error('Error executing action:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to execute action',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const toggleVerification = async (partnerId) => {
    try {
      const { default: apiClient } = await import('../../request/http-common');
      const response = await apiClient.post(`/api/admin/p2p/partners/${partnerId}/toggle-verification`);
      
      if (response.data && response.data.success) {
        const partner = partners.find(p => p.id === partnerId);
        const newStatus = !partner?.is_verified;
        
        setSnackbar({
          open: true,
          message: `Partner ${newStatus ? 'verified' : 'unverified'} successfully`,
          severity: 'success'
        });

        fetchPartners();
        onUpdate && onUpdate();
      } else {
        throw new Error(response.data?.message || 'Failed to toggle verification');
      }
    } catch (error) {
      console.error('Error toggling verification:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to toggle verification',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'pending':
        return 'Under Review';
      case 'rejected':
        return 'Rejected';
      case 'suspended':
        return 'Suspended';
      default:
        return status;
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handlePageChange = (event, page) => {
    setPagination(prev => ({ ...prev, current_page: page }));
  };

  return (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filter Partners
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                placeholder="Search partners..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Verification</InputLabel>
                <Select
                  value={filters.is_verified}
                  onChange={(e) => handleFilterChange('is_verified', e.target.value)}
                  label="Verification"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="1">Verified</MenuItem>
                  <MenuItem value="0">Unverified</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                placeholder="Country..."
                value={filters.country}
                onChange={(e) => handleFilterChange('country', e.target.value)}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Partners Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Partner</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Orders</TableCell>
              <TableCell>Volume</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {partners.map((partner) => (
              <TableRow key={partner.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {partner.partner_name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {partner.partner_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {partner.user?.email}
                      </Typography>
                      {partner.is_verified && (
                        <Chip
                          icon={<Star />}
                          label="Verified"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{partner.country}</TableCell>
                <TableCell>
                  <Chip
                    label={getStatusText(partner.status)}
                    color={getStatusColor(partner.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {partner.total_orders_count || 0} total
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {partner.completed_orders_count || 0} completed
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    ${(partner.total_volume || 0).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {(Number(partner.average_rating) || 0).toFixed(1)}
                    </Typography>
                    <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                    <Typography variant="caption" color="text.secondary">
                      ({partner.total_ratings || 0})
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => fetchPartnerDetails(partner.id)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    
                    {partner.status === 'pending' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleAction('approve', partner.id)}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleAction('reject', partner.id)}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    
                    {partner.status === 'approved' && (
                      <Tooltip title="Suspend">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleAction('suspend', partner.id)}
                        >
                          <Pause />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {partner.status === 'suspended' && (
                      <Tooltip title="Reactivate">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleAction('reactivate', partner.id)}
                        >
                          <PlayArrow />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    <Tooltip title={partner.is_verified ? "Remove Verification" : "Verify Partner"}>
                      <IconButton
                        size="small"
                        color={partner.is_verified ? "warning" : "primary"}
                        onClick={() => toggleVerification(partner.id)}
                        disabled={actionLoading}
                      >
                        {partner.is_verified ? <StarBorder /> : <Star />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.last_page}
            page={pagination.current_page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Partner Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Partner Details</DialogTitle>
        <DialogContent>
          {selectedPartner && (
            <Box>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Partner Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">
                          <strong>Name:</strong> {selectedPartner.partner_name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Email:</strong> {selectedPartner.user?.email}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Phone:</strong> {selectedPartner.phone || 'Not provided'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Country:</strong> {selectedPartner.country}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Status:</strong> 
                          <Chip 
                            label={getStatusText(selectedPartner.status)} 
                            color={getStatusColor(selectedPartner.status)} 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                        <Typography variant="body2">
                          <strong>Verified:</strong> {selectedPartner.is_verified ? 'Yes' : 'No'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Joined:</strong> {new Date(selectedPartner.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Statistics */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Performance Stats
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">
                          <strong>Total Orders:</strong> {selectedPartner.total_orders_count || 0}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Completed Orders:</strong> {selectedPartner.completed_orders_count || 0}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Active Rates:</strong> {selectedPartner.active_rates_count || 0}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Total Volume:</strong> ${(selectedPartner.total_volume || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Average Rating:</strong> {(Number(selectedPartner.average_rating) || 0).toFixed(1)}/5.0
                        </Typography>
                        <Typography variant="body2">
                          <strong>Total Ratings:</strong> {selectedPartner.total_ratings || 0}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Admin Notes */}
                {selectedPartner.admin_notes && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Admin Notes
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedPartner.admin_notes}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Rejection/Suspension Details */}
                {(selectedPartner.status === 'rejected' || selectedPartner.status === 'suspended') && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="error">
                          {selectedPartner.status === 'rejected' ? 'Rejection Details' : 'Suspension Details'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {selectedPartner.rejection_reason && (
                            <Typography variant="body2">
                              <strong>Reason:</strong> {selectedPartner.rejection_reason}
                            </Typography>
                          )}
                          {selectedPartner.suspension_reason && (
                            <Typography variant="body2">
                              <strong>Reason:</strong> {selectedPartner.suspension_reason}
                            </Typography>
                          )}
                          {selectedPartner.suspended_until && (
                            <Typography variant="body2">
                              <strong>Suspended Until:</strong> {new Date(selectedPartner.suspended_until).toLocaleDateString()}
                            </Typography>
                          )}
                          {selectedPartner.reviewed_at && (
                            <Typography variant="body2">
                              <strong>Reviewed At:</strong> {new Date(selectedPartner.reviewed_at).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentAction?.action === 'approve' && 'Approve Partner'}
          {currentAction?.action === 'reject' && 'Reject Partner'}
          {currentAction?.action === 'suspend' && 'Suspend Partner'}
          {currentAction?.action === 'reactivate' && 'Reactivate Partner'}
        </DialogTitle>
        <DialogContent>
          {(currentAction?.action === 'reject' || currentAction?.action === 'suspend') && (
            <TextField
              fullWidth
              label="Reason"
              multiline
              rows={3}
              value={actionData.reason}
              onChange={(e) => setActionData(prev => ({ ...prev, reason: e.target.value }))}
              sx={{ mb: 2 }}
              required
            />
          )}
          
          {currentAction?.action === 'suspend' && (
            <TextField
              fullWidth
              label="Suspension Duration (days)"
              type="number"
              value={actionData.duration}
              onChange={(e) => setActionData(prev => ({ ...prev, duration: e.target.value }))}
              sx={{ mb: 2 }}
              helperText="Leave empty for indefinite suspension"
            />
          )}
          
          <TextField
            fullWidth
            label="Admin Notes"
            multiline
            rows={2}
            value={actionData.notes}
            onChange={(e) => setActionData(prev => ({ ...prev, notes: e.target.value }))}
            helperText="Internal notes for admin reference"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setActionDialogOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={executeAction}
            color="primary"
            variant="contained"
            disabled={
              actionLoading ||
              ((currentAction?.action === 'reject' || currentAction?.action === 'suspend') && 
              !actionData.reason.trim())
            }
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            {actionLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default P2PPartnerManagement;
